function toArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  const result: string[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      result.push(item);
    }
  }
  return result;
}

function withLegalCountryFallback(value: unknown, legalCountry: string): string[] {
  const values = toArray(value);

  if (values.length > 0) {
    return values;
  }

  return legalCountry ? [legalCountry] : [];
}

export type CompanyActivityOrganizationInput = {
  get?: (key: string) => unknown;
  legalCountry?: string | null;
  legalRegistrationDate?: Date | string | null;
  businessRelationships?: readonly string[] | null;
  businessRelationshipsOther?: string | null;
  incomingTransfersOrigin?: readonly string[] | null;
  outgoingTransfersDestination?: readonly string[] | null;
};

export type CompanyActivityOrganization = {
  legalCountry: string;
  legalRegistrationDate?: Date | string | null;
  businessRelationships: string[];
  businessRelationshipsOther: string;
  incomingTransfersOrigin: string[];
  outgoingTransfersDestination: string[];
};

function readAttr(
  organization: CompanyActivityOrganizationInput,
  key: keyof Omit<CompanyActivityOrganizationInput, 'get'>
): unknown {
  if (typeof organization.get === 'function') {
    return organization.get(key);
  }

  return organization[key];
}

function hasNonEmptyStringArray(value: unknown): boolean {
  return Array.isArray(value) && value.some(item => typeof item === 'string' && item.length > 0);
}

export function hasCompanyActivityInfo(organization: { get: (key: string) => unknown }): boolean {
  const businessRelationships = organization.get('businessRelationships');
  const incoming = organization.get('incomingTransfersOrigin');
  const outgoing = organization.get('outgoingTransfersDestination');

  if (!hasNonEmptyStringArray(incoming) || !hasNonEmptyStringArray(outgoing)) {
    return false;
  }

  if (!hasNonEmptyStringArray(businessRelationships)) {
    return false;
  }

  if (Array.isArray(businessRelationships) && businessRelationships.includes('other')) {
    const other = organization.get('businessRelationshipsOther');
    return typeof other === 'string' && other.trim().length > 0;
  }

  return true;
}

export function snapshotCompanyActivityOrganization(
  organization: CompanyActivityOrganizationInput
): CompanyActivityOrganization {
  const legalCountryValue = readAttr(organization, 'legalCountry');
  const legalCountry = typeof legalCountryValue === 'string' ? legalCountryValue : '';
  const legalRegistrationDate = readAttr(organization, 'legalRegistrationDate');
  const businessRelationshipsOther = readAttr(organization, 'businessRelationshipsOther');

  return {
    legalCountry,
    legalRegistrationDate:
      legalRegistrationDate instanceof Date || typeof legalRegistrationDate === 'string'
        ? legalRegistrationDate
        : null,
    businessRelationships: toArray(readAttr(organization, 'businessRelationships')),
    businessRelationshipsOther:
      typeof businessRelationshipsOther === 'string' ? businessRelationshipsOther : '',
    incomingTransfersOrigin: withLegalCountryFallback(
      readAttr(organization, 'incomingTransfersOrigin'),
      legalCountry
    ),
    outgoingTransfersDestination: withLegalCountryFallback(
      readAttr(organization, 'outgoingTransfersDestination'),
      legalCountry
    ),
  };
}
